import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectDVCPMATrackingForecastLocationId,
  selectDVCPMATrackingForecastTimeFrequency,
  selectDVCPMATrackingForecastForecastDate,
  selectDVCPMATrackingForecastVariable,
  selectDVCPMATrackingForecastSubpage,
  selectDVCPMATrackingForecastMultiLocationIds,
  selectDVCPMATrackingForecastRounds,
  selectDVCPMATrackingForecastChartType,
} from 'redux/reducers/meteorologyAgroTrackingForecastDetailedViewControlPanelReducer';
import {
  GetChartDataParams,
} from 'types/Meteorology/Agro/TrackingForecast/DetailedView/getChartData';
import type {
  MeteorologyAgroTrackingForecastDetailedViewControlPanel as T,
} from 'redux/reducers/meteorologyAgroTrackingForecastDetailedViewControlPanelReducer';

export default function useChartDataParams() {
  const subpage: T['subpage'] = useSelector(selectDVCPMATrackingForecastSubpage);
  const chartType: T['chartType'] = useSelector(selectDVCPMATrackingForecastChartType);
  const locationId: T['locationId'] = useSelector(selectDVCPMATrackingForecastLocationId);
  const multiLocationIds: T['multiLocationIds'] = useSelector(selectDVCPMATrackingForecastMultiLocationIds);
  const timeFrequency: T['timeFrequency'] = useSelector(selectDVCPMATrackingForecastTimeFrequency);
  const forecastDate: T['forecastDate'] = useSelector(selectDVCPMATrackingForecastForecastDate);
  const rounds: T['rounds'] = useSelector(selectDVCPMATrackingForecastRounds);
  const variable: T['variable'] = useSelector(selectDVCPMATrackingForecastVariable);

  const multiLocationParams: GetChartDataParams | undefined = useMemo(() => {
    if (subpage !== 'locations') return undefined;

    const paramsAreValid = (
      multiLocationIds.length > 0
      && timeFrequency !== undefined
      && variable !== undefined
      && forecastDate.length > 0
    );

    if (!paramsAreValid) return undefined;

    return {
      subpage,
      chartType,
      locationIds: multiLocationIds,
      timeFrequency,
      variable,
      forecastDates: [forecastDate],
    };
  }, [
    subpage, chartType, multiLocationIds, timeFrequency, variable, forecastDate,
  ]);

  const multiRoundParams: GetChartDataParams | undefined = useMemo(() => {
    if (subpage !== 'rounds') return undefined;

    const paramsAreValid = (
      locationId !== undefined
      && timeFrequency !== undefined
      && variable !== undefined
      && rounds.length > 0
    );

    if (!paramsAreValid) return undefined;

    return {
      subpage,
      chartType,
      locationId,
      timeFrequency,
      variable,
      forecastDates: rounds,
    };
  }, [
    subpage, chartType, locationId, timeFrequency, variable, rounds,
  ]);

  const params: GetChartDataParams | undefined = useMemo(() => {
    switch (subpage) {
      case 'locations': return multiLocationParams;
      case 'rounds': return multiRoundParams;
      default: return undefined;
    }
  }, [subpage, multiLocationParams, multiRoundParams]);

  return params;
}
